import "./home.css";

import React from "react";

import {
  ABOUT,
  CATEGORIES,
  ESTIMATOR,
  HERO,
  PAGE_TITLE,
  PAST_EVENTS,
  QUOTE_LINK,
  SECTIONS,
  TESTIMONIALS,
} from "../../data/content";
import Lightbox from "../general/Lightbox";
import PageMeta from "../general/PageMeta";
import SectionLink from "../general/SectionLink";
import ContactSection from "./ContactSection";
import EventPopup from "./EventPopup";
import Slideshow from "./Slideshow";

const QuoteButton = ({ children }) => (
  <a
    href={QUOTE_LINK}
    target={"_blank"}
    rel={"noopener noreferrer"}
    className={"btn"}
  >
    {children}
  </a>
);

const Home = () => {
  //Photo viewer: which set of photos is open, and at which photo
  const [viewer, setViewer] = React.useState(null);
  const openViewer = (title, photos, index = 0) => setViewer({ title, photos, index });

  return (
    <React.Fragment>
      <PageMeta
        title={PAGE_TITLE}
        isFullTitle
      />

      {/* ------------------------------ HERO ------------------------------ */}
      <section className={"hero"}>
        <div className={"hero-inner"}>
          <img
            src={HERO.IMAGE}
            alt={""}
            width={1920}
            height={1080}
            className={"hero-image"}
          />
          <div className={"hero-content"}>
            <h1 className={"hero-title"}>
              {HERO.LINES[0]}
              <br />
              {HERO.LINES[1]}
            </h1>
            <QuoteButton>{HERO.BUTTON}</QuoteButton>
          </div>
        </div>
      </section>

      {/* ------------------------------ STORY ------------------------------ */}
      <section
        id={SECTIONS.ABOUT}
        className={"about"}
        aria-labelledby={"about-title"}
      >
        <div className={"about-inner"}>
          <div className={"about-card"}>
            <h2
              id={"about-title"}
              className={"about-title"}
            >
              {ABOUT.TITLE}
            </h2>
            {ABOUT.PARAGRAPHS.map((p) => (
              <p key={p.slice(0, 20)}>{p}</p>
            ))}
            <SectionLink
              section={SECTIONS.GALLERY}
              className={"btn btn-outline"}
            >
              {ABOUT.BUTTON}
            </SectionLink>
          </div>
          <div className={"about-photos"}>
            <img
              src={ABOUT.IMAGE.src}
              alt={ABOUT.IMAGE.alt}
              width={ABOUT.IMAGE.width}
              height={ABOUT.IMAGE.height}
              className={"about-photo"}
              loading={"lazy"}
            />
            <img
              src={ABOUT.PORTRAIT.src}
              alt={ABOUT.PORTRAIT.alt}
              width={ABOUT.PORTRAIT.width}
              height={ABOUT.PORTRAIT.height}
              className={"about-portrait"}
              loading={"lazy"}
            />
          </div>
        </div>
      </section>

      <Slideshow />

      {/* ------------------------------ PAST EVENTS ------------------------------ */}
      <section
        id={SECTIONS.GALLERY}
        className={"past-events"}
        aria-labelledby={"past-events-title"}
      >
        <h2
          id={"past-events-title"}
          className={"section-title"}
        >
          {PAST_EVENTS.TITLE}
        </h2>
        <ul className={"past-events-grid"}>
          {PAST_EVENTS.PHOTOS.map((photo, i) => (
            <li key={photo.src}>
              <button
                type={"button"}
                className={"past-events-item"}
                aria-label={`Open photo ${i + 1} of ${PAST_EVENTS.PHOTOS.length}`}
                onClick={() => openViewer("Past Events", PAST_EVENTS.PHOTOS, i)}
              >
                <img
                  src={photo.thumb}
                  alt={""}
                  width={600}
                  height={600}
                  loading={"lazy"}
                />
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* ------------------------------ ESTIMATOR ------------------------------ */}
      <section
        className={"estimator"}
        aria-labelledby={"estimator-title"}
      >
        <h2
          id={"estimator-title"}
          className={"section-title estimator-title"}
        >
          {ESTIMATOR.TITLE}
        </h2>
        <QuoteButton>{ESTIMATOR.BUTTON}</QuoteButton>
        <p className={"estimator-text"}>{ESTIMATOR.TEXT}</p>
      </section>

      {/* ------------------------------ TESTIMONIALS ------------------------------ */}
      <section
        className={"testimonials"}
        aria-labelledby={"testimonials-title"}
      >
        <h2
          id={"testimonials-title"}
          className={"section-title testimonials-title"}
        >
          {TESTIMONIALS.TITLE}
        </h2>
        <ul className={"testimonials-list"}>
          {TESTIMONIALS.ITEMS.map((item) => (
            <li
              key={item.name}
              className={"testimonial"}
            >
              <img
                src={item.icon}
                alt={""}
                className={"testimonial-icon"}
                width={80}
                height={120}
                loading={"lazy"}
              />
              <blockquote className={"testimonial-quote"}>
                <p>{item.quote}</p>
              </blockquote>
              <p className={"testimonial-name"}>{item.name}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* ------------------------------ EVENT TYPES ------------------------------ */}
      <section
        className={"categories"}
        aria-label={"Event galleries"}
      >
        <ul className={"categories-grid"}>
          {CATEGORIES.map((cat) => (
            <li key={cat.slug}>
              <button
                type={"button"}
                className={"category"}
                aria-label={`${cat.label} - view gallery (${cat.photos.length} photos)`}
                onClick={() => openViewer(cat.label, cat.photos)}
              >
                <span className={"category-photo"}>
                  <img
                    src={cat.cover}
                    alt={""}
                    width={600}
                    height={948}
                    loading={"lazy"}
                  />
                </span>
                <span className={"category-label"}>
                  <span className={"category-title"}>{cat.label}</span>
                  <span className={"btn btn-outline category-btn"}>View Gallery</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      <ContactSection />

      {viewer ? (
        <Lightbox
          key={viewer.title}
          title={viewer.title}
          photos={viewer.photos}
          index={viewer.index}
          onClose={() => setViewer(null)}
        />
      ) : null}
      <EventPopup />
    </React.Fragment>
  );
};

export default Home;
